import {
  DeleteTwoTone,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import {
  Affix,
  Button,
  Card,
  Dropdown,
  Menu,
  message,
  Popconfirm,
  Space,
} from "antd";
import ListTable from "../../common/ListTable";
import { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FOLDING_REQUIREMENT_DELETE,
  FOLDING_REQUIREMENT_LIST,
  UNIT_PRICE_DELETE,
} from "../../apiServices/API_ENDPOINTS";
import { deleteData, getData } from "../../apiServices/common";
import {
  FOLDING_REQUIREMENT_CREATE_PERMISSION,
  FOLDING_REQUIREMENT_EDIT_PERMISSION,
  FOLDING_REQUIREMENT_VIEW_PERMISSION,
} from "../../routes/permissions";
import { hasPermission } from "../../utils/functions";
import { isMobileView } from "../Common/AppHelper";
import CustomFilter from "../Common/CustomFilter";
import Header from "../Common/Header";

const FoldingRequirement = ({ width }) => {
  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location?.pathname;

  // States
  const [showFilter, setShowFilter] = useState(() => {
    const filterStatus = localStorage.getItem("filter_status");
    return filterStatus && routerPath
      ? JSON.parse(filterStatus)[routerPath]
      : false;
  });
  const [listData, setListData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState(10);
  const [fetchError, setFetchError] = useState(null);

  // Memoized Columns
  const columns = useMemo(
    () => [
      {
        title: "Style No.",
        dataIndex: ["style_info", "style_no"],
        key: "style_no",
      },
      {
        title: "Sub Style No.",
        dataIndex: ["sub_style_info", "sub_style_no"],
        key: "sub_style_no",
      },
      {
        title: "Reference No.",
        dataIndex: ["sales_contract_info", "reference_no"],
        key: "reference_no",
      },
      {
        title: "Buying Office Name",
        dataIndex: ["buying_office_info", "name"],
        key: "buying_office_name",
      },
      {
        title: "Buyer Name",
        dataIndex: ["buyer_info", "name"],
        key: "buyer_name",
      },
      {
        title: "PO Number",
        dataIndex: ["po_map_info", "original_po"],
        key: "po_number",
      },
      {
        title: "Delivery Date",
        dataIndex: ["po_map_info", "delivery_date"],
        key: "delivery_date",
      },
      { title: "Color", dataIndex: ["color_info", "name"], key: "color" },
      {
        title: "Folding Ratio",
        dataIndex: ["folding_requirement_polys", 0, "ratio_name"],
        key: "folding_ratio",
      },
      {
        title: "Folding Status",
        dataIndex: ["folding_requirement_polys", 0, "status"],
        key: "folding_status",
      },
      {
        title: "Created By",
        dataIndex: ["folding_requirement_polys", 0, "created_by_info", "name"],
        key: "created_by",
      },
      {
        title: "Actions",
        key: "action",
        width: "10%",
        render: (_, record) => (
          <div className="cursor-pointer">
            <Dropdown overlay={menu(record)}>
              <MoreOutlined style={{ fontSize: "14px" }} />
            </Dropdown>
          </div>
        ),
      },
    ],
    [],
  );

  // Fetch List Data
  const getListData = useCallback(
    async (filter = {}) => {
      setLoading(true);
      setFetchError(null);
      const { page: requestedPage, ...requestFilter } = filter || {};
      const pageNumber = requestedPage != null ? requestedPage : currentPage;
      if (requestedPage != null && Number(requestedPage) !== Number(currentPage)) {
        setCurrentPage(Number(requestedPage));
      }
      const customQuery = `${FOLDING_REQUIREMENT_LIST}/?page=${pageNumber}`;
      try {
        const response = await getData(customQuery, false, requestFilter);
        if (response?.data?.code === 200) {
          const masterData = response.data;
          setListData(masterData?.data?.data || []);
          setTotalData(masterData?.data?.total || 0);
          setRangeData(masterData?.data?.per_page || 10);
        } else {
          setFetchError("Could not load this list.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setFetchError(error?.message || "Could not load this list.");
      } finally {
        setLoading(false);
      }
    },
    [currentPage],
  );

  // Handle Delete
  const handleDelete = useCallback(
    async (id) => {
      if (!id) return;
      try {
        const query = `${FOLDING_REQUIREMENT_DELETE}/${id}`;
        const response = await deleteData(query);
        if (response?.code === 200) {
          message.success("Data Deleted Successfully");
          getListData();
        }
      } catch (error) {
        console.error("Error deleting data:", error);
      }
    },
    [getListData],
  );

  // Menu for Actions
  const menu = useCallback(
    (data) => (
      <Menu
        items={[
          {
            label: (
              <Button
                icon={<EyeOutlined />}
                type="text"
                size="small"
                onClick={() =>
                  navigate(
                    `/digitalization/folding-requirement/view/${data?.id}`,
                  )
                }
              >
                View
              </Button>
            ),
            key: "1",
            disabled: !hasPermission([FOLDING_REQUIREMENT_VIEW_PERMISSION]),
          },
          {
            label: (
              <Button
                icon={<EditOutlined />}
                type="text"
                size="small"
                onClick={() =>
                  navigate(
                    `/digitalization/folding-requirement/edit/${data?.id}`,
                  )
                }
              >
                Edit
              </Button>
            ),
            key: "12",
            disabled: !hasPermission([FOLDING_REQUIREMENT_EDIT_PERMISSION]),
          },
          {
            label: (
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => handleDelete(data?.id)}
              >
                <Button
                  size="small"
                  icon={<DeleteTwoTone />}
                  danger
                  type="text"
                >
                  Delete
                </Button>
              </Popconfirm>
            ),
            key: "2",
          },
        ]}
      />
    ),
    [handleDelete, navigate],
  );

  // Pagination Handler
  const paginate = useCallback((page) => setCurrentPage(page), []);

  // Memoized Filter
  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        tableData={listData}
        fetchTableData={getListData}
        filterName="folding_requirement"
      />
    ),
    [showFilter],
  );

  // Update Filter Status in LocalStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};
    parsedFilterStatus[routerPath] = showFilter;
    localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
  }, [showFilter, routerPath]);

  // Fetch Data on Mount and Page Change
  useEffect(() => {
    getListData();
  }, [currentPage, getListData]);

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}
        >
          <Header
            title="Folding Requirement"
            subTitle=""
            actions={
              <Space>
                <Button
                  type={showFilter ? "danger" : "primary"}
                  size="small"
                  onClick={() => setShowFilter((prev) => !prev)}
                >
                  {`${showFilter ? "Close" : "Open"} Filter Option`}
                </Button>
                <Button
                  key="3"
                  size="small"
                  type="primary"
                  disabled={
                    !hasPermission([FOLDING_REQUIREMENT_CREATE_PERMISSION])
                  }
                >
                  <Link to="/digitalization/folding-requirement/create">
                    {hasPermission([FOLDING_REQUIREMENT_CREATE_PERMISSION])
                      ? "Add New"
                      : "No Permission"}
                  </Link>
                </Button>
              </Space>
            }
          />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          {memoizedCustomFilter}
          <ListTable
            loading={loading}
            error={fetchError}
            onRetry={() => getListData()}
            emptyText="No records to show yet."
            dataSource={listData}
            columns={columns}
            scroll={{ x: true }}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: paginate,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
};

export default FoldingRequirement;
